// Hand-written: a photograph with its selling points floating over it.
//
// Figma 6377:88784, and the same idea again at 6377:89068. This is the type the section
// inventory said was missing: both nodes were filed under `feature-cards`, which is a
// card grid and is not this.
//
// The design's own layer names are `Feature List` and `Feature Item`, and they give the
// component exactly: a glass tray - 10% white, a 5px blur, a hairline border, `Radius/xl`
// - holding white pills at `Radius/m`, each 16px of padding with a 24px green tick and
// 14px of black text. The tick is the exported `check-circle`.
//
// **Two placement decisions, because the file hand-places these and a CMS cannot.**
//
// 1. The design floats two trays at hand-picked coordinates - three pills at the middle
//    left, two at the top right. Reproducing arbitrary coordinates from a flat list of
//    items is not something an editor could reason about, so the list splits in two: the
//    first half sits top-right, the rest bottom-left. Deterministic, and it reads as the
//    same composition.
// 2. **Below `lg` the pills drop out of the photograph and sit under it.** White text
//    boxes over a photograph in a 375px column cover the subject and become unreadable
//    the moment the words are longer than the mock-up's. The photo is the backdrop on a
//    desktop and an illustration on a phone.
import { SectionShell, Heading, Lead } from '../primitives';
import { Icon } from '../../components/ui/Icon';
import { Image, SIZES } from '../../components/ui/Image';
import type { PhotoHighlightsSection, Tag } from '../../types/sections';

/** Figma 6377:88787 - the glass tray and its pills. */
function Tray({ items, className }: { items: Tag[]; className?: string }) {
  if (!items.length) return null;

  return (
    <ul
      className={`flex flex-col gap-2 rounded-xl border border-white/10 bg-white/10 p-2 backdrop-blur-[5px] ${className ?? ''}`}
    >
      {items.map((item, i) => (
        <li key={i} className="flex items-center gap-4 rounded-m bg-white p-4">
          <Icon name="check-circle" size={24} className="shrink-0 text-green-600" />
          <span className="text-body-sm text-ink-strong">{item.label}</span>
        </li>
      ))}
    </ul>
  );
}

/**
 * The same highlights with no photograph to float over: the keyword band `tag-band` used
 * to be. Bordered pills on the page's own background, at a size a person can read - the
 * file draws these at 8px over a marketing image, which is decoration, not content.
 *
 * No hash is added for the editor. `tag-band` used to prepend one, which was invisible
 * magic and wrong for the tray's own pills ("Bezpieczne logowanie z SSO" is not a
 * hashtag); an editor who wants `#wygodnie` writes `#wygodnie`.
 */
function Band({ items }: { items: Tag[] }) {
  return (
    <ul className="flex flex-wrap gap-2">
      {items.map((item, i) => (
        <li
          key={i}
          className="rounded-full border border-grey-500 bg-surface px-6 py-2 text-body-sm text-ink"
        >
          {item.label}
        </li>
      ))}
    </ul>
  );
}

export default function PhotoHighlights({ data }: { data: PhotoHighlightsSection }) {
  const items = data.highlights ?? [];
  // The file's own split is three on the left and two on the right, so the larger group
  // goes left when the count is odd.
  const half = Math.ceil(items.length / 2);
  const first = items.slice(0, half);
  const rest = items.slice(half);
  const secondary = data.secondaryMedia?.image;
  const photo = data.media?.image;

  // No photograph: the highlights are the section. This is what `tag-band` was, and its
  // own header said the file has no frame showing it - a row of keywords is reusable, the
  // 8px it is drawn at over a picture is not.
  if (!photo) {
    if (!items.length) return null;
    return (
      <SectionShell settings={data.settings} name="photo-highlights">
        {data.heading ? <Heading>{data.heading}</Heading> : null}
        {data.lead ? <Lead>{data.lead}</Lead> : null}
        <Band items={items} />
      </SectionShell>
    );
  }

  return (
    <SectionShell settings={data.settings} name="photo-highlights">
      {data.heading ? <Heading>{data.heading}</Heading> : null}
      {data.lead ? <Lead>{data.lead}</Lead> : null}

      <div className="flex flex-col gap-4 lg:flex-row lg:items-end">
        <figure className="relative flex-1 overflow-hidden rounded-m bg-surface-sunken">
          <div className="aspect-[4/3] lg:aspect-[1061/765]">
            <Image
              media={photo}
              alt={data.media?.alt ?? ''}
              sizes={secondary ? SIZES.third : SIZES.full}
              className="h-full w-full object-cover"
            />
          </div>

          {/* Only over the photograph from `lg` up; below that they follow it. The first
              items take the left tray, which is the one a reader meets first. */}
          <Tray items={first} className="absolute left-[11%] top-1/2 hidden w-[288px] -translate-y-1/2 lg:flex" />
          <Tray items={rest} className="absolute right-[16%] top-[8%] hidden w-[288px] lg:flex" />
        </figure>

        {secondary ? (
          <figure className="aspect-square overflow-hidden rounded-m bg-surface-sunken lg:w-[283px]">
            <Image
              media={secondary}
              alt={data.secondaryMedia?.alt ?? ''}
              sizes="283px"
              className="h-full w-full object-cover"
            />
          </figure>
        ) : null}
      </div>

      {items.length ? <Tray items={items} className="lg:hidden" /> : null}
    </SectionShell>
  );
}
